import { IndexBasedGridSelection, IndexBasedSelectionData, NumRange, SelectionBehavior } from '../IndexBasedGridSelection';
import { TreeGridSelectionUtils } from './TreeGridSelectionUtils';

/**
 * TreeGridSelection is used to track the selected items in the grid who are
 * hidden because their parent is in a collapsed state. It inherits from
 * IndexBasedGridSelection, which is used to track the visible selections.
 */
export class TreeGridSelection extends IndexBasedGridSelection {
  /**
   * domain indexes of selected items that are hidden because their parent is collapsed
   * Note this' parent class, IndexBasedSelectionDataState, stores the UI indexes (grid-based indexes)
   * of the visible selections.
   */
  private hiddenSelection: IndexBasedSelectionData;

  public constructor(rows: number, cols: number) {
    super(rows, cols);
    this.hiddenSelection = {
      rowRanges: [],
      colRanges: [],
      cellRanges: [],
      gridDimensions: null as any, // hiddenSelection doesn't get selection events so it doesn't need dimensions
    };

    this.selectColumnOnHeaderClick = false;
    this.selectionBehavior = SelectionBehavior.SelectRow;
  }

  public clearSelection(): void {
    super.clearSelection();
    this.clearHiddenSelection();
  }

  public initializeSelection(selectionData: IndexBasedSelectionData): void {
    this.clearHiddenSelection();
    super.initializeSelection(selectionData);
  }

  public initializeHiddenSelection(hiddenSelectionData: IndexBasedSelectionData): void {
    this.hiddenSelection = hiddenSelectionData;
  }

  public getHiddenSelection(): IndexBasedSelectionData {
    return this.hiddenSelection;
  }

  private clearHiddenSelection(): void {
    if (this.hiddenSelection) {
      this.hiddenSelection.rowRanges = [];
      this.hiddenSelection.colRanges = [];
      this.hiddenSelection.cellRanges = [];
    }
  }

  /** @returns a new array containing the indexes of all selected domain items, including those that are
   * hidden because they belong to a parent that is in a collapsed state.
   */
  public getSelectedDomainIndexes(convertToDomainIndex: (gridRowIndex: number) => number): number[] {
    let domainIndexes: number[] = [];
    TreeGridSelectionUtils.forEachRowIndexInSelection(
      this.getSelection(),
      index => domainIndexes.push(convertToDomainIndex(index))
    );

    TreeGridSelectionUtils.forEachRowIndexInSelection(this.getHiddenSelection(), index => domainIndexes.push(index));
    return domainIndexes;
  }

  /**
   * When a row is expanded, any hidden selections are pulled from hiddenSelection, converted to grid-based ranges, and
   * pushed onto the regular selection.
   * When a row is collapsed, any selected children of the row are moved from the regular selection to the hidden selection.
   * @param isExpanded - true if the row is being expanded; false if it's being collapsed
   * @param gridRowIndex - the index of the row in the grid
   * @param numChildren - how many children belong to this row
   * @param convertToDomainIndex - method used to convert grid row indexes to domain indexes.
   */
  public handleRowExpansion = (
    isExpanded: boolean,
    gridRowIndex: number,
    numChildren: number,
    convertGridRowIndexToDomainIndex: (gridRowIndex: number) => number
  ): boolean => {
    if (numChildren <= 0) {
      return false; // nothing to do if there are no children
    }
    let hiddenDomainRanges = this.getHiddenSelection().rowRanges;
    let visibleGridRanges = this.getSelection().rowRanges;

    let selectionIsChanged = false;
    if (isExpanded) {
      TreeGridSelectionUtils.splitRanges(visibleGridRanges, gridRowIndex, gridRowIndex, numChildren);
      let domainIndex = convertGridRowIndexToDomainIndex(gridRowIndex);
      let domainRangeOfChildren: NumRange = {begin: domainIndex + 1, end: domainIndex + numChildren};
      selectionIsChanged = TreeGridSelectionUtils.unhideDomainSelections(
        hiddenDomainRanges,
        visibleGridRanges,
        gridRowIndex + 1,
        domainRangeOfChildren
      );
    } else {
      let extractedRanges: NumRange[] = TreeGridSelectionUtils.splitRanges(
        visibleGridRanges,
        gridRowIndex,
        gridRowIndex + numChildren,
        -numChildren
      );

      selectionIsChanged = extractedRanges.length > 0;
      extractedRanges.forEach(range => {
        hiddenDomainRanges.push({begin: convertGridRowIndexToDomainIndex(range.begin), end: convertGridRowIndexToDomainIndex(range.end)});
      });
    }

    return selectionIsChanged;
  }
}
